import { useSearchParams } from "react-router-dom";

type UseTabFromUrlProps<T extends string> = {
  param: string;
  defaultTab: T;
  tabs: readonly T[];
  history?: "push" | "replace";
};

const isTabValue = <T extends string>(
  value: string | null,
  tabs: readonly T[]
) => {
  return value !== null && tabs.includes(value as T);
};

export default function useTabFromUrl<T extends string>({
  param,
  defaultTab,
  tabs,
  history = "replace",
}: UseTabFromUrlProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  // 1. Read raw value from URL
  const rawValue = searchParams.get(param);

  // 2. Validate
  const isValid = isTabValue(rawValue, tabs);

  // 3. Resolve final tab (URL is source of truth, fallback if invalid)
  const tab = isValid ? (rawValue as T) : defaultTab;

  // 4. Setter (event-driven, no side effects during render)
  const setTab = (nextTab: T) => {
    // guard: invalid value
    if (!tabs.includes(nextTab)) return;

    // guard: same value
    if (nextTab === tab) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(param, nextTab);

    setSearchParams(nextParams, {
      replace: history === "replace",
    });
  };

  return {
    tab,
    setTab,
  };
}
